import React from "react";

function OrderDetails({ order }) {
  if (!order)
    return (
      <div className="text-center text-gray-500 py-10">
        No order details available.
      </div>
    );

  const {
    _id,
    status,
    createdAt,
    updatedAt,
    amount,
    appliedCoupon,
    totalCouponDiscount,
    totalOfferDiscount,
    address,
    products = [],
    user,
  } = order;

  // Status badge color
  const statusColor =
    {
      "Not processed": "bg-gray-200 text-gray-700",
      Processing: "bg-blue-100 text-blue-700",
      Shipped: "bg-yellow-100 text-yellow-700",
      Delivered: "bg-green-100 text-green-700",
      Cancelled: "bg-red-100 text-red-700",
    }[status] || "bg-gray-200 text-gray-700";

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">
            Order Details
          </h1>
          <p className="text-gray-500 text-sm">
            Order ID: <span className="font-mono">{_id}</span>
          </p>
        </div>
        <span
          className={`inline-block px-4 py-1 rounded-full text-sm font-semibold shadow-sm ${statusColor}`}
        >
          {status}
        </span>
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Order Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Order Summary
          </h2>
          <div className="flex flex-col gap-2 text-gray-600">
            <div>
              <span className="font-medium">Created:</span>{" "}
              {new Date(createdAt).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Updated:</span>{" "}
              {new Date(updatedAt).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Total Amount:</span>{" "}
              <span className="text-xl font-bold text-indigo-600">
                {amount} EGP
              </span>
            </div>
            <div>
              <span className="font-medium">Coupon:</span>{" "}
              {appliedCoupon ? (
                <span className="text-green-600">{appliedCoupon}</span>
              ) : (
                <span className="text-gray-400">None</span>
              )}
            </div>
            <div>
              <span className="font-medium">Coupon Discount:</span>{" "}
              <span className="text-red-500">{totalCouponDiscount} EGP</span>
            </div>
            <div>
              <span className="font-medium">Offer Discount:</span>{" "}
              <span className="text-red-400">{totalOfferDiscount} EGP</span>
            </div>
          </div>
        </div>
        {/* Address Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Shipping Address
          </h2>
          <div className="flex flex-col gap-2 text-gray-600">
            <div>
              <span className="font-medium">Title:</span> {address?.title}
            </div>
            <div>
              <span className="font-medium">Street:</span> {address?.street}
            </div>
            <div>
              <span className="font-medium">City:</span> {address?.city}
            </div>
            <div>
              <span className="font-medium">Governorate:</span> {address?.state}
            </div>
            <div>
              <span className="font-medium">Default:</span>{" "}
              {address?.isDefault ? (
                <span className="text-green-600">Yes</span>
              ) : (
                <span className="text-gray-400">No</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Products in Order
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-2 px-3 font-semibold text-gray-500">#</th>
                <th className="py-2 px-3 font-semibold text-gray-500">Name</th>
                <th className="py-2 px-3 font-semibold text-gray-500">Price</th>
                <th className="py-2 px-3 font-semibold text-gray-500">
                  Quantity
                </th>
                <th className="py-2 px-3 font-semibold text-gray-500">Total</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, idx) => (
                <tr
                  key={p._id || idx}
                  className="border-b border-gray-100 hover:bg-gray-50 transition"
                >
                  <td className="py-2 px-3">{idx + 1}</td>
                  <td className="py-2 px-3 font-medium text-gray-800">
                    {p.name}
                  </td>
                  <td className="py-2 px-3 text-indigo-600">{p.price} EGP</td>
                  <td className="py-2 px-3">{p.quantity}</td>
                  <td className="py-2 px-3 font-semibold text-gray-700">
                    {p.price * p.quantity} EGP
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Info Card (optional) */}
      {user && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            User Information
          </h2>
          <div className="flex flex-col gap-2 text-gray-600">
            <div>
              <span className="font-medium">Name:</span> {user.name}
            </div>
            <div>
              <span className="font-medium">Email:</span> {user.email}
            </div>
            <div>
              <span className="font-medium">Role:</span>{" "}
              {user.role === 1 ? (
                <span className="text-indigo-600">Admin</span>
              ) : (
                <span className="text-gray-700">Registered User</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderDetails;
